import { z } from 'zod';
import { BusinessContextSchema, LedgerPolaritySchema } from './BusinessAnalysisSchemas.js';
/**
 * Zod schemas for Report Specifications (DTOs only).
 *
 * PURPOSE:
 *
 * These schemas define the wire format for report specifications in:
 * - API requests/responses
 * - YAML file serialization (localStorage, clipboard export)
 * - Database storage
 *
 * ARCHITECTURE:
 *
 * - **Zod Types (this file)**: DTOs for serialization boundaries only
 *   - Validation via Zod schemas when crossing boundaries
 *   - JSON-serializable plain objects
 *   - NOT used for UI state or business logic
 *
 * - **Domain Classes** (@multiplier/lib-platform-business-analysis):
 *   - Immutable with private readonly fields, only getters
 *   - Used for UI state (React useState) and business operations
 *   - Type-safe mutation methods (insertRow, removeRow, updateRow)
 *   - Converted to/from Zod DTOs via Injection at boundaries
 *
 * USAGE:
 *
 * ```typescript
 * // Fetch from API: DTO → Domain
 * const apiData = await fetchReportSpec(); // Zod DTO
 * const spec = entityReportSpecificationInjection.invert(apiData).getOrThrow(); // Domain class
 *
 * // UI state: Domain classes
 * const [spec, setSpec] = useState<EntityReportSpecification>(initialSpec);
 * setSpec(prev => prev.updateRow(path, updater).getOrThrow());
 *
 * // Save to API: Domain → DTO
 * const dto = entityReportSpecificationInjection.apply(spec); // Zod DTO
 * await saveReportSpec(dto);
 * ```
 */
/**
 * Reference to a Xero account (line item from Xero report).
 * The account name will be pulled from the Xero report at runtime.
 */
export const XeroReferenceSpecificationSchema = z
    .object({
    type: z.literal('reference'),
    source: z.literal('xero_account'),
    id: z.string().brand().describe('Xero account ID'),
})
    .strict();
/**
 * Reference to an uploaded CSV account (line item from CSV/Excel data).
 * The account name will be pulled from the uploaded data at runtime.
 */
export const UploadedCSVReferenceSpecificationSchema = z
    .object({
    type: z.literal('reference'),
    source: z.literal('uploaded_csv'),
    id: z.string().brand().describe('CSV account ID'),
})
    .strict();
/**
 * Reference to another row defined earlier in the same report.
 * Used for calculated sections that aggregate previously defined rows.
 * The row ID must refer to a row defined earlier in the specification (lexical ordering).
 */
export const SelfReferenceSpecificationSchema = z
    .object({
    type: z.literal('reference'),
    source: z.literal('self'),
    id: z.string().brand().describe('ID of the row to reference (must be defined earlier in the file)'),
})
    .strict();
/**
 * ImportRef is a branded string in the format "entity[reportId]" that references
 * an external report to be imported into a consolidated report.
 * Example: "onside[fcf]" references the "fcf" report from the "onside" entity.
 *
 * Format: `<entityName>[<reportId>]` where both parts are non-empty and contain no brackets.
 */
export const ImportRefSchema = z
    .string()
    .regex(/^[^[\]]+\[[^[\]]+\]$/, 'ImportRef must be in format "entity[reportId]"')
    .brand();
/**
 * Reference to rows from imported reports.
 * When processed, creates a section that aggregates the specified row from each imported report.
 */
export const ImportsReferenceSpecificationSchema = z
    .object({
    type: z.literal('reference'),
    source: z.literal('imports'),
    id: z
        .string()
        .brand()
        .describe('Unique identifier for this section in the current report. ' +
        'This ID is used to reference this section from other parts of the same report.'),
    name: z.string().brand().describe('Display name for this section'),
    sourceId: z
        .string()
        .brand()
        .describe('ID of the row to pull from each imported report. ' +
        'This references a row defined in the imported reports, not in the current report.'),
    ledgerPolarity: LedgerPolaritySchema.describe('Ledger polarity for the resulting section children'),
})
    .strict();
// schema for arbitrary decimal value, positive or negative, with an arbitrary number of decimal places
export const DecimalSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'Amount must be a valid number');
export const TimesScalarSpecificationSchema = z
    .object({
    type: z.literal('times_scalar'),
    scalar: DecimalSchema.describe('Scalar multiplier'),
    referencedRowID: z.string().brand().describe('ID of the row to reference'),
    id: z.string().brand().describe('ID of the new row'),
    name: z.string().brand().describe('Name of the new row'),
})
    .strict();
/**
 * Internal union of all reference specification types.
 * Discriminated on 'source' field for type-safe narrowing.
 * Not exported - use RowSpecification instead.
 */
const ReferenceSpecificationSchema = z.discriminatedUnion('source', [
    XeroReferenceSpecificationSchema,
    UploadedCSVReferenceSpecificationSchema,
    SelfReferenceSpecificationSchema,
    ImportsReferenceSpecificationSchema,
]);
/**
 * Union of row specifications that can be ignored.
 * These are external data source references (Xero accounts or uploaded CSV accounts)
 * that the user has marked as "mapped but not included in the report".
 * Discriminated on 'source' field for type-safe narrowing.
 */
export const IgnoredRowSpecificationSchema = z.discriminatedUnion('source', [
    XeroReferenceSpecificationSchema,
    UploadedCSVReferenceSpecificationSchema,
]);
/**
 * Section specification with explicitly defined children.
 * Represents a hierarchical grouping of rows whose value is the sum of its children.
 */
export const SectionSpecificationSchema = z
    .object({
    type: z.literal('section'),
    name: z.string().brand().describe('Display name for this section'),
    id: z.string().brand().describe('Unique identifier for this section'),
    children: z
        .lazy(() => RowGroupSpecificationSchema)
        .describe('Child rows to aggregate'),
})
    .strict();
/**
 * Union of all row specification types.
 * Discriminated on 'type' field for type-safe narrowing.
 */
export const RowSpecificationSchema = z.discriminatedUnion('type', [
    SectionSpecificationSchema,
    ReferenceSpecificationSchema,
    TimesScalarSpecificationSchema,
]);
/**
 * Group of rows with a common ledger polarity.
 * The polarity determines how ledger balances are displayed (positive/negative).
 */
export const RowGroupSpecificationSchema = z
    .object({
    ledgerPolarity: LedgerPolaritySchema.describe('Ledger polarity for this group'),
    rows: z.array(RowSpecificationSchema).describe('Row specifications in this group'),
})
    .strict();
/**
 * Entity report specification - defines how to build a report for a specific entity.
 * This is the main configuration object that gets serialized to YAML.
 */
export const EntityReportSpecificationSchema = z
    .object({
    reportId: z.string().brand().describe('Report identifier (unique within entity)'),
    reportName: z.string().brand().describe('Human-readable report name'),
    rowGroup: RowGroupSpecificationSchema.describe('Root row group specification'),
    ignored: z
        .array(IgnoredRowSpecificationSchema)
        .default([])
        .describe('External references marked as mapped but not included in the report'),
    imports: z
        .array(ImportRefSchema)
        .default([])
        .describe('Array of imported reports in "entity[reportId]" format (e.g., ["onside[fcf]"])'),
})
    .strict();
/**
 * Schema for a collection of entity report specifications keyed by business context.
 * Keys are branded as 'BusinessContext' at parse time.
 */
export const ZodEntityReportSpecificationsSchema = z.record(BusinessContextSchema, z.array(EntityReportSpecificationSchema).describe('Array of report specifications for this business context'));
/**
 * File-level schema for YAML files containing multiple entity report specifications.
 * Structure: { BusinessContexts: { [businessContext]: EntityReportSpecification[] } }
 */
export const ReportSpecificationFileSchema = z
    .object({
    BusinessContexts: ZodEntityReportSpecificationsSchema,
})
    .strict();
//# sourceMappingURL=ReportSpecificationSchemas.js.map